/**
 * Turning the demand the orders place on the store into rows you can read.
 *
 * The rule that lives here rather than in the screen, because getting it wrong
 * renders as a plausible number rather than as an error: **pieces are never
 * netted against kilograms.** Nothing in the system records how much cloth a
 * piece takes, so a row can say "41 pieces wanted" and "62.40 kg on hand" side
 * by side and must not go on to say which is bigger. `coverage()` grades the
 * *shelf*, not the request.
 *
 * Lines whose fabric was never chosen do not reach here at all — the server
 * leaves them out, because a line that names no cloth names no shelf.
 */

import { isLowStock } from './fabricShelves';

/**
 * What the store holds against each demand row.
 *
 * `onHand` is `useFabricBalances().onHand` — the same lookup the order form
 * uses, so a row that names no print reads as the cloth across every print it
 * comes in (`exact: false`) rather than as an empty shelf. Null until the
 * balances arrive: "not asked yet" and "nothing on the shelf" must not render
 * the same.
 */
export const withOnHand = (rows, onHand) => (rows || []).map((row) => ({
  ...row,
  held: onHand ? onHand(row.fabricTypeId, row.fabricColorId, row.fabricPatternId) : null,
}));

/**
 * How the shelf behind a row is doing — on its own terms, in kilograms.
 *
 * Deliberately says nothing about whether the shelf covers the request: it
 * cannot, and a grade that implied it would be read as one.
 *
 *   unknown — the balances never arrived
 *   empty   — the shelf is bare, and there is live demand on it
 *   low     — under the store's own low-stock mark
 *   ok      — stocked
 */
export const coverage = (row) => {
  if (!row?.held) return 'unknown';
  if (!(row.held.weight > 0)) return 'empty';
  return isLowStock(row.held.weight) ? 'low' : 'ok';
};

/**
 * The orderings the by-fabric view offers.
 *
 *   pieces  — most wanted first. What the store is being asked for.
 *   urgency — soonest delivery first. What it is being asked for *now*.
 *   stock   — the shelves that cannot answer, first: bare before low before
 *             stocked, and a row the balances never covered last.
 *
 * Every comparator falls back to pieces, so rows that tie sort the same way
 * twice rather than shuffling between renders.
 */
export const SORTS = ['pieces', 'urgency', 'stock'];

const COVERAGE_RANK = { empty: 0, low: 1, ok: 2, unknown: 3 };

export const sortRequests = (rows, key) => {
  const byPieces = (a, b) => (b.pieces || 0) - (a.pieces || 0);
  const list = [...(rows || [])];

  if (key === 'urgency') {
    return list.sort((a, b) => {
      // A row with no date sorts last rather than first: an absent deadline is
      // the one thing that is certainly not urgent, and '' < any date string.
      if (!a.earliestDelivery && !b.earliestDelivery) return byPieces(a, b);
      if (!a.earliestDelivery) return 1;
      if (!b.earliestDelivery) return -1;
      if (a.earliestDelivery !== b.earliestDelivery) {
        return a.earliestDelivery < b.earliestDelivery ? -1 : 1;
      }
      return byPieces(a, b);
    });
  }

  if (key === 'stock') {
    return list.sort((a, b) => {
      const rank = COVERAGE_RANK[coverage(a)] - COVERAGE_RANK[coverage(b)];
      return rank !== 0 ? rank : byPieces(a, b);
    });
  }

  return list.sort(byPieces);
};

/**
 * A shelf named the way the store names it: fabric, print, colour, with the
 * parts nobody recorded left out rather than printed as "Unspecified".
 *
 * "Printed" is a complete answer for a cloth whose colour was never keyed in —
 * printing the placeholder beside it would read as a colour called Unspecified
 * rather than as a blank.
 */
export const shelfLabel = (row) => {
  if (!row) return '';

  const parts = [row.fabric, row.pattern, row.color]
    .filter((part) => part && part !== 'Unspecified');

  return parts.length ? parts.join(' · ') : 'Not specified';
};
